import { Injectable } from "@angular/core";
import { Appointment } from "../../models/appointment.model";
import { BehaviorSubject, of } from "rxjs";
import * as moment from 'moment-timezone';
import * as utils from '../../utils/utility-functions';
import { appointmentlistenum } from "../../models/appointment-list-type";
import { LogService } from '../logging.service';
import { LogType } from "../../models/log-type";
import { EmailNotificationService } from "../notification.service";
import { RelevantLabService } from "../relevant-lab.service";
import { AngularFirestore, AngularFirestoreCollection } from "@angular/fire/compat/firestore";
import { UserTypeEnum } from "src/app/models/user-type";
import { NotificationType } from "src/app/models/notification-type.model";
import { AngularFireFunctions } from "@angular/fire/compat/functions";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { switchMap } from "rxjs/operators";
import * as momentTZ from 'moment-timezone';

@Injectable({
  providedIn: 'root',
})
export class AppointmentCollectionService {
  private firebaseAppointmentCollection: AngularFirestoreCollection;
  private firebaseOrgLocationCollection: AngularFirestoreCollection;
  private firebaseOrgLocationPointsCollection: AngularFirestoreCollection;
  private firebaseExpertPointsCollection: AngularFirestoreCollection;
  private firebaseDoctorPointsCollection: AngularFirestoreCollection;

  _EXPERT = 'Expert';
  _HOSPITAL = 'Hospital';
  _CLOSED = 'closed';
  _REVIEW = 'review';
  _OPEN = 'open';

  private allAppointments = new BehaviorSubject([]);
  currentAllAppointments = this.allAppointments.asObservable();

  private allUpcomingAppointments = new BehaviorSubject([]);
  currentAllUpcomingAppointments = this.allUpcomingAppointments.asObservable();

  private allUpcomingSoonAppointments = new BehaviorSubject([]);
  currentAllUpcomingSoonAppointments = this.allUpcomingSoonAppointments.asObservable();

  private allPastAppointments = new BehaviorSubject([]);
  currentAllPastAppointments = this.allPastAppointments.asObservable();

  private allPastNotJoinedAppointments = new BehaviorSubject([]);
  currentAllPastNotJoinedAppointments = this.allPastNotJoinedAppointments.asObservable();

  private unconfirmedAppointments = new BehaviorSubject([]);
  currentUncomfirmedAppointments = this.unconfirmedAppointments.asObservable();

  private unconfirmedIssuesAppointments = new BehaviorSubject([]);
  currentUncomfirmedIssuesAppointments = this.unconfirmedIssuesAppointments.asObservable();

  private allUnconfirmedAppointments = new BehaviorSubject([]);
  currentAllUncomfirmedAppointments = this.allUnconfirmedAppointments.asObservable();

  private reviewAppointments = new BehaviorSubject([]);
  currentReviewAppointments = this.reviewAppointments.asObservable();

  private confirmedInSessionAppointments = new BehaviorSubject([]);
  currentConfirmedInSessionAppointments = this.confirmedInSessionAppointments.asObservable();

  private urgentAppointments = new BehaviorSubject([]);
  currentUrgentAppointments = this.urgentAppointments.asObservable();

  private appointment = new BehaviorSubject(null);
  currentAppointment = this.appointment.asObservable();

  private allAppointmentsForPatient = new BehaviorSubject([]);
  currentAllAppointmentsForPatient = this.allAppointmentsForPatient.asObservable();

  private allAppointmentsForExpert = new BehaviorSubject([]);
  currentAllAppointmentsForExpert = this.allAppointmentsForExpert.asObservable();

  private allAppointmentsForDoctor = new BehaviorSubject([]);
  currentAllAppointmentsForDoctor = this.allAppointmentsForDoctor.asObservable();

  private allClosedAppointments = new BehaviorSubject([]);
  currentAllClosedAppointments = this.allClosedAppointments.asObservable();

  private allExpertClosedAppointments = new BehaviorSubject([]);
  currentAllExpertClosedAppointments = this.allExpertClosedAppointments.asObservable();

  private allDoctorClosedAppointments = new BehaviorSubject([]);
  currentAllDocrorClosedAppointments = this.allDoctorClosedAppointments.asObservable();

  private closedReportCredits = new BehaviorSubject([]);
  currentClosedReportCredits = this.closedReportCredits.asObservable();

  private expertClosedReportCredits = new BehaviorSubject([]);
  currentExpertClosedReportCredits = this.expertClosedReportCredits.asObservable();

  private doctorClosedReportCredits = new BehaviorSubject([]);
  currentDoctorClosedReportCredits = this.doctorClosedReportCredits.asObservable();

  constructor(
    private angularFunctions: AngularFireFunctions,
    private notificationService: EmailNotificationService,
    private logService: LogService,
    private relevantLabService: RelevantLabService,
    private angularFirestore: AngularFirestore,
    private afAuth: AngularFireAuth
  ) {
  }

  ///////////////////////////
  /*  Get() appointments */
  ///////////////////////////
  async getAllAppointmentsForPatient(patientId: string) {
    try {
      let appointments = [];

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .where('patient_id', '==', patientId)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            appointments.push(docData)
          });
        }).catch(err => console.log("getAllAppointmentsForPatient query 1 error: " + err));

      this.sortPatientAppointments(appointments);

      return appointments;
    } catch (error) {
      console.log(error);
    }
  }

  async getAllAppointmentsForExpert(expertID: string) {
    try {
      let appointments = [];

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .where('expert_id', '==', expertID)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            appointments.push(docData)
          });
        }).catch(err => console.log("getAllAppointmentsForExpert query 1 error: " + err));

      this.sortExpertAppointments(appointments);

      return appointments;
    } catch (error) {
      console.log(error);
    }
  }

  async getAllAppointmentsForDoctor(doctorID: string) {
    try {
      let appointments = [];

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .where('doctor_id', '==', doctorID)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            appointments.push(docData)
          });
        }).catch(err => console.log("getUrgentAppointments query 1 error: " + err));

      this.sortDoctorAppointments(appointments);

      return appointments;
    } catch (error) {
      console.log(error);
    }
  }

  async findAppointmentByID(appointmentID: string) {
    try {
      let appointment = null;

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .doc(appointmentID)
        .get()
        .then(querySnapshot => {
          const docData = {
            id: querySnapshot.id,
            ...querySnapshot.data()
          }
          appointment = docData;
        }).catch(err => console.log("findAppointmentByID query 1 error: " + err));

      this.updateAppointment(appointment);
      return appointment;
    } catch (error) {
      console.log(error);
    }
  }

  findAppointmentByID$(appointmentID: string) {
    return this.afAuth.authState.pipe(
      switchMap(user => {
        if (user) {
          return this.angularFirestore.doc<Appointment>(`Appointments/${appointmentID}`).valueChanges({ idField: 'id' });
        } else {
          return of(null);
        }
      })
    );
  }

  async getUrgentAppointments() {
    try {
      let appointments = [];

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .where('urgent', '==', true)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            appointments.push(docData)
          });
        }).catch(err => console.log("getUrgentAppointments query 1 error: " + err));

      this.sortUrgentAppointments(appointments);

      return appointments;
    } catch (error) {
      console.log(error);
    }
  }

  async getUnconfirmedAppointments() {
    try {
      let appointments = [];
      this.unconfirmedAppointments.next(appointments);

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .where('confirmed', '==', false)
        .where('consultation', 'array-contains', this._OPEN)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            appointments.push(docData)
          });
        }).catch(err => console.log("getUnconfirmedAppointments query 1 error: " + err));

      this.sortUnconfirmedAppointments(appointments);

      return appointments;
    } catch (error) {
      console.log(error);
    }
  }

  async getPastNotJoinedAppointments() {
    try {
      const now = moment().format('x');
      let appointments = [];
      this.updateAppointmentAndSort(appointments, appointmentlistenum.PAST_NOT_JOINED);

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      /* Expert not joined */
      await this.firebaseAppointmentCollection.ref
        .where('confirmed', '==', true)
        .where('appointment_date_timestamp', '<=', now)
        .where('consultation', 'array-contains', this._OPEN)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData: any = {
              id: doc.id,
              ...doc.data()
            }
            if (docData.expert_join_time == null || docData.hospital_join_time == null) {
              appointments.push(docData)
            }
          });
        }).catch(err => console.log("getPastNotJoinedAppointments query 1 error: " + err));

      this.updateAppointmentAndSort(appointments, appointmentlistenum.PAST_NOT_JOINED);
    } catch (error) {
      console.log(error);
    }
  }

  async getAllUpcomingSoonAppointments() {
    try {
      const now = moment().format('x');
      const tenMinutesLater = moment().add(10, 'minutes').format('x');
      let appointments = [];
      this.updateAppointmentAndSort(appointments, appointmentlistenum.COMING_UP);

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .where('confirmed', '==', true)
        .where('appointment_date_timestamp', '<=', tenMinutesLater)
        .where('appointment_date_timestamp', '>', now)
        .where('consultation', 'array-contains', this._OPEN)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData: any = {
              id: doc.id,
              ...doc.data()
            }
            if (docData.expert_join_time == null && docData.hospital_join_time == null) {
              appointments.push(docData)
            }
          });
        }).catch(err => console.log("getAllUpcomingSoonAppointments query 1 error: " + err));

      this.updateAppointmentAndSort(appointments, appointmentlistenum.COMING_UP);
    } catch (error) {
      console.log(error);
    }
  }

  async getAllUncomfirmedIssuesAppointments() {
    try {
      let now = moment().format('x');
      let timeAhead = moment().add(120, 'minutes').format('x');
      let appointments = [];
      this.updateAppointmentAndSort(appointments, appointmentlistenum.UNCONFIRMED_ISSUES);

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .where('confirmed', '==', false)
        .where('appointment_date_timestamp', '<', timeAhead)
        .where('appointment_date_timestamp', '>', now)
        .where('consultation', 'array-contains', this._OPEN)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData: any = {
              id: doc.id,
              ...doc.data()
            }
            // if updated_at has been over 15 minutes ago, push to array
            if (moment().diff(moment(docData.updated_at, 'x'), 'minutes') > 15) {
              appointments.push(docData)
            }
          });
        }).catch(err => console.log("getAllUncomfirmedIssuesAppointmentss query 1 error: " + err));

      this.updateAppointmentAndSort(appointments, appointmentlistenum.UNCONFIRMED_ISSUES);
    } catch (error) {
      console.log(error);
    }
  }

  async getAllConfirmedInSessionAppointments() {
    try {
      const fiveMintesFromNow = moment().add(5, 'minutes').format('x');
      const fifteenMinutesAgo = moment().subtract(15, 'minutes').format('x');
      let appointments = [];
      this.updateAppointmentAndSort(appointments, appointmentlistenum.CONFIRMED_IN_SESSION);

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .where('confirmed', '==', true)
        .where('appointment_date_timestamp', '<=', fiveMintesFromNow)
        .where('appointment_date_timestamp', '>=', fifteenMinutesAgo)
        .where('consultation', 'array-contains-any', [this._OPEN, this._REVIEW])
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData: any = {
              id: doc.id,
              ...doc.data()
            }
            // check if expert and hospital have joined
            if (docData.expert_join_time != null && docData.hospital_join_time != null) {
              appointments.push(docData)
            }
          });
        }).catch(err => console.log("getAllConfirmedInSessionAppointments query 1 error: " + err));

      this.updateAppointmentAndSort(appointments, appointmentlistenum.CONFIRMED_IN_SESSION);
    } catch (error) {
      console.log(error);
    }
  }

  getDoctorMessages(id: string) {
    return this.afAuth.authState.pipe(
      switchMap(user => {
        if (user) {
          return this.angularFirestore.collection(`Appointments/${id}/DoctorMessages`, ref =>
            ref.orderBy('created_at', 'asc')
          )
            .valueChanges({ idField: 'id' });
        } else {
          return of(null);
        }
      })
    );
  }

  getExpertMessages(id: string) {
    return this.afAuth.authState.pipe(
      switchMap(user => {
        if (user) {
          return this.angularFirestore.collection(`Appointments/${id}/ExpertMessages`, ref =>
            ref.orderBy('created_at', 'asc')
          )
            .valueChanges({ idField: 'id' });
        } else {
          return of(null);
        }
      })
    );
  }

  getLocationMessages(id: string) {
    return this.afAuth.authState.pipe(
      switchMap(user => {
        if (user) {
          return this.angularFirestore.collection(`Appointments/${id}/LocationMessages`, ref =>
            ref.orderBy('created_at', 'asc')
          )
            .valueChanges({ idField: 'id' });
        } else {
          return of(null);
        }
      })
    );
  }

  // getPastNotJoinedAppointments() {
  //   const now = moment().format('x');

  //   return this.afAuth.authState.pipe(
  //     switchMap(user => {
  //       if (user) {
  //         return this.angularFirestore.collection('Appointments', ref => ref
  //           .where('confirmed', '==', true)
  //           .where('appointment_date_timestamp', '<=', now)
  //           .where('consultation', 'array-contains', this._OPEN)
  //           .where('expert_join_time', '==', null)
  //           .where('hospital_join_time', '==', null)
  //           .where('expert_id', '==', user.uid)
  //           .orderBy('date', 'desc')
  //           .orderBy('time', 'desc')
  //         ).valueChanges({ idField: 'id' })
  //       }
  //     }))
  //   }

  async getAllAppointments() {
    try {
      let appointments = [];

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            appointments.push(docData)
          });
        }).catch(err => console.log("getAllAppointments query 1 error: " + err));

      this.sortAppointments(appointments);

      return appointments;
    } catch (error) {
      console.log(error);
    }
  }

  ///////////////////////////
  /*  Get() reports */
  ///////////////////////////

  async getAllClosedReport() {
    try {
      let appointments = [];
      let locations = [];

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      if (this.firebaseOrgLocationCollection == null) {
        this.firebaseOrgLocationCollection = this.angularFirestore.collection('OrgLocation');
      }

      if (this.firebaseOrgLocationPointsCollection == null) {
        this.firebaseOrgLocationPointsCollection = this.angularFirestore.collection('OrgLocationPoints');
      }

      await this.firebaseOrgLocationPointsCollection.ref
        .orderBy('org_location_name', 'asc')
        .where('month', '==', moment().month() + 1)
        .where('year', '==', moment().year())
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            locations.push(docData);
          });
        }).catch(err => console.log("getAllClosedReport firebaseOrgLocationCollection query 1 error: " + err));

      await this.firebaseAppointmentCollection.ref
        .where('consultation', 'array-contains', this._CLOSED)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            appointments.push(docData)
          });
        }).catch(err => console.log("getAllClosedReport query 1 error: " + err));

      this.updateClosedReports(appointments);
    } catch (error) {
      console.log('getAllClosedReport(): ' + JSON.stringify(error));
    }
  }

  async getClosedReportMonth(month: any, year: any) {
    try {
      let locations = [];
      this.updateClosedReportCredits(locations);

      if (this.firebaseOrgLocationPointsCollection == null) {
        this.firebaseOrgLocationPointsCollection = this.angularFirestore.collection('OrgLocationPoints');
      }

      await this.firebaseOrgLocationPointsCollection.ref
        .orderBy('org_location_name', 'asc')
        .where('month', '==', month)
        .where('year', '==', year)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            locations.push(docData);
          });
        }).catch(err => console.log("getAllClosedReport firebaseOrgLocationCollection query 1 error: " + err));

      this.updateClosedReportCredits(locations);

      return true;
    } catch (error) {
      console.log('getAllClosedReport(): ' + JSON.stringify(error));
    }
  }

  async getExpertClosedReportMonth(month: any, year: any) {
    try {
      let experts = [];
      this.updateExpertClosedReportCredits(experts);

      if (this.firebaseExpertPointsCollection == null) {
        this.firebaseExpertPointsCollection = this.angularFirestore.collection('ExpertPoints');
      }

      await this.firebaseExpertPointsCollection.ref
        .orderBy('expert_name', 'asc')
        .where('month', '==', month)
        .where('year', '==', year)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            experts.push(docData);
          });
        }).catch(err => console.log("getExpertClosedReportMonth firebaseExpertPointsCollection query 1 error: " + err));

      this.updateExpertClosedReportCredits(experts);

      return true;
    } catch (error) {
      console.log('getAllClosedReport(): ' + JSON.stringify(error));
    }
  }

  async getDoctorClosedReportMonth(month: any, year: any) {
    try {
      let doctors = [];
      this.updateDoctorClosedReportCredits(doctors);

      if (this.firebaseDoctorPointsCollection == null) {
        this.firebaseDoctorPointsCollection = this.angularFirestore.collection('DoctorPoints');
      }

      await this.firebaseDoctorPointsCollection.ref
        .orderBy('doctor_name', 'asc')
        .where('month', '==', month)
        .where('year', '==', year)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            doctors.push(docData);
          });
        }).catch(err => console.log("getDoctorClosedReportMonth firebaseDoctorPointsCollection query 1 error: " + err));

      this.updateDoctorClosedReportCredits(doctors);

      return true;
    } catch (error) {
      console.log('getAllClosedReport(): ' + JSON.stringify(error));
    }
  }

  async getClosedAppointmentsForMonth(hospitalID: any, date: any) {
    try {
      let appointments = [];

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      console.log("getClosedAppointmentsForMonth date: " + date)

      await this.firebaseAppointmentCollection.ref
        .where('org_location_id', '==', hospitalID)
        .where('date_closed', '>=', momentTZ(date, 'MMMM YYYY').startOf('month').tz('America/Los_Angeles').format('x'))
        .where('date_closed', '<=', momentTZ(date, 'MMMM YYYY').endOf('month').tz('America/Los_Angeles').format('x'))
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            appointments.push(docData);
          });
        }).catch(err => console.log("getClosedAppointmentsForMonth firebaseAppointmentCollection query 1 error: " + err));

        console.log("getClosedAppointmentsForMonth appointments: " + appointments);

      this.updateClosedReports(appointments);
    } catch (error) {
      console.log('getAllClosedReport(): ' + JSON.stringify(error));
    }
  }

  async getExpertClosedAppointmentsForMonth(expertID: any, date: any) {
    try {
      let appointments = [];
      this.updateExpertClosedReports(appointments);

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .where('expert_id', '==', expertID)
        .where('date_closed', '>=', momentTZ(date).startOf('month').tz('America/Los_Angeles').format('x'))
        .where('date_closed', '<=', momentTZ(date).endOf('month').tz('America/Los_Angeles').format('x'))
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            appointments.push(docData);
          });
        }).catch(err => console.log("getExpertClosedAppointmentsForMonth firebaseAppointmentCollection query 1 error: " + err));

      this.updateExpertClosedReports(appointments);
    } catch (error) {
      console.log('getAllClosedReport(): ' + JSON.stringify(error));
    }
  }

  async getDoctorClosedAppointmentsForMonth(doctorID: any, date: any) {
    try {
      let appointments = [];
      this.updateDoctorClosedReports(appointments);

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .where('doctor_id', '==', doctorID)
        .where('date_closed', '>=', momentTZ(date).startOf('month').tz('America/Los_Angeles').format('x'))
        .where('date_closed', '<=', momentTZ(date).endOf('month').tz('America/Los_Angeles').format('x'))
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            appointments.push(docData);
          });
        }).catch(err => console.log("getDoctorClosedAppointmentsForMonth firebaseAppointmentCollection query 1 error: " + err));

      this.updateDoctorClosedReports(appointments);
    } catch (error) {
      console.log('getDoctorClosedAppointmentsForMonth(): ' + JSON.stringify(error));
    }
  }

  async getAppointmentReport(month: any) {
    try {
      let newMonth = month;

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .where('consultation', 'array-contains-any', [this._CLOSED])
        .where('date_closed', '>=', month.start)
        .where('date_closed', '<=', month.end)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            newMonth.count++
          });
        })
        .catch(err => console.log("getAppointmentReport query 1 error: " + err));

      await this.firebaseAppointmentCollection.ref
        .where('consultation', 'array-contains-any', [this._CLOSED])
        .where('date_closed', '>=', month.start)
        .where('date_closed', '<=', month.end)
        .where('urgent', '==', true)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            newMonth.urgentCount++
          });
        })
        .catch(err => console.log("getAppointmentReport query 2 error: " + err));

      await this.firebaseAppointmentCollection.ref
        .where('consultation', 'array-contains-any', [this._CLOSED])
        .where('date_closed', '>=', month.start)
        .where('date_closed', '<=', month.end)
        .where('urgent', '==', false)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            newMonth.nonUrgentCount++
          });
        })
        .catch(err => console.log("getAppointmentReport query 3 error: " + err));

      return newMonth;
    } catch (error) {
      console.log('getAppointmentReport(): ' + JSON.stringify(error));
    }
  }

  ///////////////////////////
  /*  Check() functions */
  ///////////////////////////
  async checkIfUrgentTimeIsBooked(time) {
    try {
      let appointmentTemp = this.appointment.getValue();

      let appointmentCount = 0;

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .where('doctor_id', '==', appointmentTemp.doctor_id)
        .where('canceled', '==', false)
        .where('appointment_date_id', '==', time.id)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            appointmentCount++;
          });
        }).catch(err => console.log("checkIfUrgentTimeIsBooked query 1 error: " + err));

      return appointmentCount;
    } catch (error) {
      console.log('checkIfUrgentTimeIsBooked(): ' + JSON.stringify(error));
    }
  }

  async checkIfTimeIsBooked(time: any, appointment: any) {
    try {
      let appointmentCount = 0;

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .where('doctor_id', '==', appointment.doctor_id)
        .where('canceled', '==', false)
        .where('appointment_date_id', '==', time.id)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            appointmentCount++;
          });
        }).catch(err => console.log("checkIfUrgentTimeIsBooked query 1 error: " + err));

      return appointmentCount;
    } catch (error) {
      console.log('checkIfUrgentTimeIsBooked(): ' + JSON.stringify(error));
    }
  }

  async checkIfExpertTimeIsBooked(time: any, expertID: string) {
    try {
      let appointments = 0;

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .where('expert_id', '==', expertID)
        .where('appointment_date_timestamp', '==', time.timestamp.toString())
        .where('canceled', '==', false)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            appointments++;
          });
        }).catch(err => console.log("checkIfExpertTimeIsBooked query 1 error: " + err));

      return appointments;
    } catch (error) {
      console.log('checkIfExpertTimeIsBooked(): ' + JSON.stringify(error));
    }
  }

  async checkIfDoctorTimeIsBooked(time: any, doctorID: string) {
    try {
      let appointments = 0;

      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .where('doctor_id', '==', doctorID)
        .where('appointment_date_timestamp', '==', time.timestamp.toString())
        .where('canceled', '==', false)
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            appointments++;
          });
        }).catch(err => console.log("checkIfDoctorTimeIsBooked query 1 error: " + err));

      return appointments;
    } catch (error) {
      console.log('checkIfDoctorTimeIsBooked(): ' + JSON.stringify(error));
    }
  }

  ///////////////////////////
  /*  Save() functions */
  ///////////////////////////

  async updateAppointmentAsync(appointment: any) {
    try {
      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .doc(appointment.id)
        .update({
          expectation_resolved: true,
          admin_resolution_notes: appointment.admin_resolution_notes,
          updated_at: moment().format('x')
        }).catch(err => console.log("updateAppointmentAsync update 1 error: " + err));

      this.appointment.next(appointment);
    } catch (error) {
      console.log('updateAppointmentAsync(): ' + JSON.stringify(error));
    }
  }

  async resubmitAppointment(appointment: Appointment) {
    try {
      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      let newAppointment = utils.createAppointmentObject(appointment);

      let { id, ...appointmentToSave } = appointment;
      appointmentToSave.updated_at = moment().format('x')

      await this.firebaseAppointmentCollection.ref
        .doc(appointment.id)
        .update(JSON.parse(JSON.stringify(appointmentToSave)))
        .catch(err => console.log("resubmitAppointment query 1 error: " + err));


      this.updateAppointment(newAppointment);
      return newAppointment;
    } catch (error) {
      console.log(error);
    }
  }

  async saveAppointment(appointment: any) {
    try {
      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      let { id, ...appointmentToSave } = appointment;
      appointmentToSave.confirmed = false;

      if (appointmentToSave.created_at == null) {
        appointmentToSave.created_at = moment().format('x');
      }

      if (appointmentToSave.updated_at == null) {
        appointmentToSave.updated_at = moment().format('x');
      }

      await this.firebaseAppointmentCollection
        .add(JSON.parse(JSON.stringify(appointmentToSave)))
        .then(function (docRef) {
          appointmentToSave.id = docRef.id;
        })
        .catch(err => console.log("saveAppointment query 1 error: " + err));

      if (appointmentToSave.id != null) {
        this.relevantLabService.saveBookingLabs(appointmentToSave.id);
        this.logService.createAppointmentLog(appointmentToSave.id, LogType.APPOINTMENT_CREATED);

        const convertedTimestamp = moment(appointmentToSave.appointment_date_timestamp, 'x').format('MMM D LT');

        const emailMessage = appointmentToSave.appointment_date_timestamp != null && convertedTimestamp != null
          ? `${appointmentToSave.doctor_first_name} ${appointmentToSave.doctor_last_name} requested a consult on ${convertedTimestamp}.`
          : `${appointmentToSave.doctor_first_name} ${appointmentToSave.doctor_last_name} requested a consult.`;

        this.notificationService.sendEmailAndPush(
          UserTypeEnum.EXPERT,
          appointmentToSave.expert_id,
          'WTD - Consult Requested',
          emailMessage,
          NotificationType.APPOINTMENT_GENERAL,
          appointmentToSave.id
        );
      }

      return appointmentToSave;
    } catch (error) {
      console.log('saveAppointment(): ' + JSON.stringify(error));
    }
  }

  async adminUpdateAppointmentStatus(appointment: any) {
    try {
      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      let { id, ...appointmentToSave } = appointment;
      appointmentToSave.urgent = appointment.urgent;
      appointmentToSave.urgent_confirmed = appointment.urgent_confirmed;

      if (appointmentToSave.updated_at == null) {
        appointmentToSave.updated_at = moment().format('x');
      }

      await this.firebaseAppointmentCollection.ref
        .doc(appointment.id)
        .update({
          urgent: appointmentToSave.urgent,
          urgent_confirmed: appointmentToSave.urgent_confirmed,
          updated_at: moment().format('x')
        }).catch(err => console.log("adminUpdateAppointmentStatus query 1 error: " + err));

      const convertedTimestamp = moment(appointmentToSave.appointment_date_timestamp, 'x').format('MMM D LT');

      const emailMessage = appointmentToSave.appointment_date_timestamp != null && convertedTimestamp != null
        ? `Admin updated a consult on ${convertedTimestamp} to ${appointmentToSave.urgent ? 'Urgent' : 'Non-Urgent'}.`
        : `Admin updated a consult to ${appointmentToSave.urgent ? 'Urgent' : 'Non-Urgent'}.`;

      this.notificationService.sendEmailAndPush(
        UserTypeEnum.EXPERT,
        appointmentToSave.expert_id,
        'WTD - Consult Status Updated',
        emailMessage,
        NotificationType.APPOINTMENT_GENERAL,
        appointment.id
      );

      this.notificationService.sendEmailAndPush(
        UserTypeEnum.DOCTOR,
        appointmentToSave.doctor_id,
        'WTD - Consult Status Updated',
        emailMessage,
        NotificationType.APPOINTMENT_GENERAL,
        appointment.id
      );


      return appointmentToSave;
    } catch (error) {
      console.log('saveAppointment(): ' + JSON.stringify(error));
    }
  }

  async saveUrgentAppointment(appointment) {
    try {
      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      let updatedAppointment = appointment;
      updatedAppointment.consultation[0] = 'open';
      updatedAppointment.is_custom_time = false;
      updatedAppointment.needs_confirmation_by = this._EXPERT;

      let { id, ...appointmentToSave } = updatedAppointment;

      if (appointmentToSave.created_at == null) {
        appointmentToSave.created_at = moment().format('x');
      }

      if (appointmentToSave.updated_at == null) {
        appointmentToSave.updated_at = moment().format('x');
      }

      await this.firebaseAppointmentCollection
        .add(JSON.parse(JSON.stringify(appointmentToSave)))
        .then(function (docRef) {
          updatedAppointment.id = docRef.id;
        })
        .catch(err => console.log("saveUrgentAppointment query 1 error: " + err));

      if (updatedAppointment.id != null) {
        this.logService.createAppointmentLog(updatedAppointment.id, LogType.APPOINTMENT_CREATED);
        this.relevantLabService.saveBookingLabs(updatedAppointment.id);
      }

      return updatedAppointment;
    } catch (error) {
      console.log('saveUrgentAppointment(): ' + JSON.stringify(error));
    }
  }

  async requestAdditionalInfo(appointment: Appointment) {
    try {
      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      let currentAppointment = appointment;

      let confirmedAppointment = utils.createAppointmentObject(currentAppointment);
      confirmedAppointment.needs_confirmation_by = this._HOSPITAL;
      confirmedAppointment.info_requested = true;
      confirmedAppointment.updated_at = moment().format('x')


      await this.firebaseAppointmentCollection.ref
        .doc(appointment.id)
        .update({
          info_requested: true,
          needs_confirmation_by: this._HOSPITAL,
          expert_request: currentAppointment.expert_request
        }).catch(err => console.log("requestAdditionalInfo query 1 error: " + err));

      this.appointment.next(utils.createAppointment(confirmedAppointment));
      return confirmedAppointment;
    } catch (error) {
      console.log('requestAdditionalInfo(): ' + JSON.stringify(error));
    }
  }

  async confirmAppointmentRequest(appointment: Appointment) {
    try {
      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      let confirmedAppointment = utils.createAppointmentObject(appointment);
      confirmedAppointment.needs_confirmation_by = this._HOSPITAL;
      confirmedAppointment.updated_at = moment().format('x')

      await this.firebaseAppointmentCollection.ref
        .doc(appointment.id)
        .update({
          needs_confirmation_by: this._HOSPITAL,
          confirmed: confirmedAppointment.confirmed,
          urgent_confirmed: confirmedAppointment.urgent_confirmed,
          info_requested: confirmedAppointment.info_requested,
          zoom_start: confirmedAppointment.zoom_start ? confirmedAppointment.zoom_start : null,
          zoom_join: confirmedAppointment.zoom_join ? confirmedAppointment.zoom_join : null,
        }).catch(err => console.log("confirmAppointmentRequest update 1 error: " + err));

      this.appointment.next(utils.createAppointment(confirmedAppointment));
      return confirmedAppointment;
    } catch (error) {
      console.log('confirmAppointmentRequest(): ' + JSON.stringify(error));
    }
  }

  async updateAppointmentTime(appointment: Appointment) {
    try {
      if (this.firebaseAppointmentCollection == null) {
        this.firebaseAppointmentCollection = this.angularFirestore.collection('Appointments');
      }

      await this.firebaseAppointmentCollection.ref
        .doc(appointment.id)
        .update({
          needs_confirmation_by: this._HOSPITAL,
          updated_at: moment().format('x'),
          confirmed: appointment.confirmed,
          urgent_confirmed: appointment.urgent_confirmed,
          appointment_date: appointment.appointment_date,
          appointment_date_id: appointment.appointment_date_id,
          appointment_date_timestamp: appointment.appointment_date_timestamp,
        }).catch(err => console.log("updateAppointmentTime update 1 error: " + err));

      this.appointment.next(utils.createAppointment(appointment));
      return appointment;
    } catch (error) {
      console.log('updateAppointmentTime(): ' + JSON.stringify(error));
    }
  }

  async submitDoctorMessage(appointmentID: string, message: string) {
    try {
      const user = await this.afAuth.currentUser;

      const messageObject = {
        appointment_id: appointmentID,
        message: message,
        username: (await user).email,
        created_at: moment().format('x'),
      };

      await this.angularFirestore.collection(`Appointments/${appointmentID}/DoctorMessages`).ref
        .add({ ...messageObject })
        .catch(err => console.log("submitDoctorMessage save 1 error: " + err));

      return true;
    } catch (error) {
      console.log('submitDoctorMessage(): ' + JSON.stringify(error));
    }
  }

  async submitExpertMessage(appointmentID: string, message: string) {
    try {
      const user = await this.afAuth.currentUser;

      const messageObject = {
        appointment_id: appointmentID,
        message: message,
        username: (await user).email,
        created_at: moment().format('x'),
      };

      await this.angularFirestore.collection(`Appointments/${appointmentID}/ExpertMessages`).ref
        .add({ ...messageObject })
        .catch(err => console.log("submitExpertMessage save 1 error: " + err));

      return true;
    } catch (error) {
      console.log('submitExpertMessage(): ' + JSON.stringify(error));
    }
  }

  async submitLocationMessage(appointmentID: string, message: string) {
    try {
      const user = await this.afAuth.currentUser;

      const messageObject = {
        appointment_id: appointmentID,
        message: message,
        username: (await user).email,
        created_at: moment().format('x'),
      };

      await this.angularFirestore.collection(`Appointments/${appointmentID}/LocationMessages`).ref
        .add({ ...messageObject })
        .catch(err => console.log("submitLocationMessage save 1 error: " + err));

      return true;
    } catch (error) {
      console.log('submitLocationMessage(): ' + JSON.stringify(error));
    }
  }

  ///////////////////////////
  /*  test() functions */
  ///////////////////////////

  async test() {
    try {
      const callable = this.angularFunctions.httpsCallable('testExpertCreditReport');

      callable({})
        .toPromise()
        .then((result) => console.log('test', result))
        .catch((error) => console.log('test', error));

      return null;
    } catch (error) {
      console.log('test(): ' + JSON.stringify(error));
    }
  }

  ///////////////////////////
  /*  helper() functions */
  ///////////////////////////

  updateClosedReportCredits(locations: any) {
    this.closedReportCredits.next(locations);
  }

  updateExpertClosedReportCredits(experts: any) {
    this.expertClosedReportCredits.next(experts);
  }

  updateDoctorClosedReportCredits(doctors: any) {
    this.doctorClosedReportCredits.next(doctors);
  }

  updateClosedReports(appointments: any) {
    let newAppointmentList = [];

    appointments.forEach(appointment => {
      let newAppointment = utils.createAppointment(appointment);
      if (newAppointment.appointment_date != null && newAppointment.appointment_date != '') {
        newAppointment.date_string = moment(appointment.appointment_date).format('D MMM YYYY');
        newAppointment.time_string = moment(appointment.appointment_date).format('LT');
      } else {
        newAppointment.date_string = 'Unconfirmed';
        newAppointment.time_string = 'Unconfirmed';
      }

      newAppointmentList.push(newAppointment)
    });
    const sortedAppointments = newAppointmentList.sort((a, b) => parseInt(moment(a.appointment_date).format('x').toString()) - parseInt(moment(b.appointment_date).format('x').toString()));
    this.allClosedAppointments.next(sortedAppointments);
  }


  updateExpertClosedReports(appointments: any) {
    let newAppointmentList = [];

    appointments.forEach(appointment => {
      let newAppointment = utils.createAppointment(appointment);
      if (newAppointment.appointment_date != null && newAppointment.appointment_date != '') {
        newAppointment.date_string = moment(appointment.appointment_date).format('D MMM YYYY');
        newAppointment.time_string = moment(appointment.appointment_date).format('LT');
      } else {
        newAppointment.date_string = 'Unconfirmed';
        newAppointment.time_string = 'Unconfirmed';
      }

      newAppointmentList.push(newAppointment)
    });
    const sortedAppointments = newAppointmentList.sort((a, b) => parseInt(moment(a.appointment_date).format('x').toString()) - parseInt(moment(b.appointment_date).format('x').toString()));
    this.allExpertClosedAppointments.next(sortedAppointments);
  }

  updateDoctorClosedReports(appointments: any) {
    let newAppointmentList = [];

    appointments.forEach(appointment => {
      let newAppointment = utils.createAppointment(appointment);
      if (newAppointment.appointment_date != null && newAppointment.appointment_date != '') {
        newAppointment.date_string = moment(appointment.appointment_date).format('D MMM YYYY');
        newAppointment.time_string = moment(appointment.appointment_date).format('LT');
      } else {
        newAppointment.date_string = 'Unconfirmed';
        newAppointment.time_string = 'Unconfirmed';
      }

      newAppointmentList.push(newAppointment)
    });
    const sortedAppointments = newAppointmentList.sort((a, b) => parseInt(moment(a.appointment_date).format('x').toString()) - parseInt(moment(b.appointment_date).format('x').toString()));
    this.allDoctorClosedAppointments.next(sortedAppointments);
  }

  sortPatientAppointments(appointments: any) {
    let newAppointments = [];
    appointments.forEach(appt => {
      const newAppt = utils.createAppointment(appt);
      newAppt.date_string = moment(appt.appointment_date).format('D MMM YYYY');
      newAppt.time_string = moment(appt.appointment_date).format('LT');
      newAppointments.push(newAppt);
    });
    this.allAppointmentsForPatient.next(newAppointments);
  }

  sortExpertAppointments(appointments: any) {
    let newAppointments = [];
    appointments.forEach(appt => {
      const newAppt = utils.createAppointment(appt);
      newAppt.date_string = moment(appt.appointment_date).format('D MMM YYYY');
      newAppt.time_string = moment(appt.appointment_date).format('LT');
      newAppointments.push(newAppt);
    });
    this.allAppointmentsForExpert.next(newAppointments);
  }

  sortDoctorAppointments(appointments: any) {
    let newAppointments = [];
    appointments.forEach(appt => {
      const newAppt = utils.createAppointment(appt);
      newAppt.date_string = moment(appt.appointment_date).format('D MMM YYYY');
      newAppt.time_string = moment(appt.appointment_date).format('LT');
      newAppointments.push(newAppt);
    });
    this.allAppointmentsForDoctor.next(newAppointments);
  }

  updateAppointment(appointment: any) {
    let newAppointment = utils.createAppointment(appointment);
    this.appointment.next(newAppointment);
  }

  sortUnconfirmedAppointments(scheduleCollection) {
    let unconfirmedAppointmentsTemp: Appointment[] = [];

    for (let i = 0; i < scheduleCollection.length; i++) {
      let appointment = utils.createAppointment(scheduleCollection[i]);
      if (appointment.appointment_date != null && appointment.appointment_date != '') {
        appointment.date_string = moment(appointment.appointment_date).format('D MMM YYYY');
        appointment.time_string = moment(appointment.appointment_date).format('LT');
      } else {
        appointment.date_string = 'Unconfirmed';
        appointment.time_string = 'Unconfirmed';
      }

      unconfirmedAppointmentsTemp.push(appointment);
    }

    this.updateAppointmentAndSort(unconfirmedAppointmentsTemp, appointmentlistenum.UNCONFIRMED_APPOINTMENTS);
  }

  sortUrgentAppointments(scheduleCollection) {
    let urgentAppointmentsTemp: Appointment[] = [];

    for (let i = 0; i < scheduleCollection.length; i++) {
      let appointment = utils.createAppointment(scheduleCollection[i]);
      if (appointment.appointment_date != null && appointment.appointment_date != '') {
        appointment.date_string = moment(appointment.appointment_date).format('D MMM YYYY');
        appointment.time_string = moment(appointment.appointment_date).format('LT');
      } else {
        appointment.date_string = 'Unconfirmed';
        appointment.time_string = 'Unconfirmed';
      }

      urgentAppointmentsTemp.push(appointment);
    }

    this.updateAppointmentAndSort(urgentAppointmentsTemp, appointmentlistenum.URGENT_APPOINTMENTS);
  }

  sortAppointments(scheduleCollection) {
    let appointmentsTemp: Appointment[] = [];
    let unconfirmedAppointmentsTemp: Appointment[] = [];
    let allUnconfirmedAppointmentsTemp: Appointment[] = [];
    let reviewAppointmentsTemp: Appointment[] = [];
    let confirmedInSessionAppointmentsTemp: Appointment[] = [];
    let urgentAppointmentsTemp: Appointment[] = [];
    let allUpcomingAppointmentsTemp: Appointment[] = [];
    let allUpcomingSoonAppointmentsTemp: Appointment[] = [];
    let allPastAppointmentsTemp: Appointment[] = [];
    let allPastNotJoinedAppointmentsTemp: Appointment[] = [];
    let unconfirmedIssuesTemp: Appointment[] = [];


    for (let i = 0; i < scheduleCollection.length; i++) {
      let pastTime = false;
      let appointment = utils.createAppointment(scheduleCollection[i]);

      //Check if time is within 15 minutes
      let momentDate = moment(appointment.appointment_date);
      let updatedAt = moment(appointment.updated_at, 'x');
      let currentTime = moment();
      if (appointment.appointment_date != null && appointment.appointment_date != '') {
        appointment.date_string = moment(appointment.appointment_date).format('D MMM YYYY');
        appointment.time_string = moment(appointment.appointment_date).format('LT');
      } else {
        appointment.date_string = 'Unconfirmed';
        appointment.time_string = 'Unconfirmed';
      }
      let timeDiff = momentDate.diff(currentTime, 'minutes');

      let timeSinceUpdated = updatedAt.diff(currentTime, 'minutes');
      let timeUntilAppointment = momentDate.diff(currentTime, 'minutes');

      if (timeDiff < -15) {
        pastTime = true;
      }


      if (!appointment.confirmed && timeSinceUpdated < -120 && appointment.consultation[0] === 'open'
        || !appointment.confirmed && timeUntilAppointment < 120 && appointment.consultation[0] === 'open' && timeSinceUpdated < -15) {
        unconfirmedIssuesTemp.push(appointment);
      }

      if (appointment.confirmed && timeDiff <= 10 && timeDiff > 0) {
        allUpcomingSoonAppointmentsTemp.push(appointment);
      }

      if (appointment.confirmed && timeDiff <= 0 && timeDiff > -15 && appointment.joined) {
        confirmedInSessionAppointmentsTemp.push(appointment);
      }


      if (appointment.urgent) {
        urgentAppointmentsTemp.push(appointment);
      }

      if (pastTime) {
        allPastAppointmentsTemp.push(appointment);
      } else {
        allUpcomingAppointmentsTemp.push(appointment);
      }

      if (!appointment.confirmed && appointment.consultation[0] == 'open' && !pastTime) {
        unconfirmedAppointmentsTemp.push(appointment);
      }

      if (appointment.confirmed && pastTime && appointment.expert_join_time == null && appointment.consultation[0] != 'closed' ||
        appointment.confirmed && pastTime && appointment.hospital_join_time == null && appointment.consultation[0] != 'closed') {
        allPastNotJoinedAppointmentsTemp.push(appointment);
      }

      if (!appointment.confirmed) {
        allUnconfirmedAppointmentsTemp.push(appointment);
      }

      if (appointment.confirmed && pastTime && appointment.consultation[0] != 'closed') {
        reviewAppointmentsTemp.push(appointment);
      }

      appointmentsTemp.push(appointment);

    }

    this.updateAppointmentAndSort(appointmentsTemp, appointmentlistenum.ALL_APPOINTMENTS);
    this.updateAppointmentAndSort(unconfirmedAppointmentsTemp, appointmentlistenum.UNCONFIRMED_APPOINTMENTS);
    this.updateAppointmentAndSort(allUnconfirmedAppointmentsTemp, appointmentlistenum.UNCONFIRMED_PAST);
    this.updateAppointmentAndSort(reviewAppointmentsTemp, appointmentlistenum.REVIEW_APPOINTMENTS);
    this.updateAppointmentAndSort(urgentAppointmentsTemp, appointmentlistenum.URGENT_APPOINTMENTS);
    this.updateAppointmentAndSort(allUpcomingAppointmentsTemp, appointmentlistenum.ALL_UPCOMING_APPOINTMENTS);
    this.updateAppointmentAndSort(allPastAppointmentsTemp, appointmentlistenum.ALL_PAST_APPOINTMENTS);
    this.updateAppointmentAndSort(allPastNotJoinedAppointmentsTemp, appointmentlistenum.PAST_NOT_JOINED);
    this.updateAppointmentAndSort(allUpcomingSoonAppointmentsTemp, appointmentlistenum.COMING_UP);
    this.updateAppointmentAndSort(confirmedInSessionAppointmentsTemp, appointmentlistenum.CONFIRMED_IN_SESSION);
    this.updateAppointmentAndSort(unconfirmedIssuesTemp, appointmentlistenum.UNCONFIRMED_ISSUES);
  }

  resetAppointments() {
    this.updateAppointmentAndSort([], appointmentlistenum.ALL_APPOINTMENTS);
    this.updateAppointmentAndSort([], appointmentlistenum.UNCONFIRMED_APPOINTMENTS);
    this.updateAppointmentAndSort([], appointmentlistenum.REVIEW_APPOINTMENTS)
    this.updateAppointmentAndSort([], appointmentlistenum.URGENT_APPOINTMENTS);
    this.updateAppointmentAndSort([], appointmentlistenum.ALL_UPCOMING_APPOINTMENTS);
    this.updateAppointmentAndSort([], appointmentlistenum.ALL_PAST_APPOINTMENTS);
  }

  updateAppointmentAndSort(appointments: any[], appointmentList) {
    // Sort appointments before saving
    const sorteddAppointments = appointments.sort((a, b) => parseInt(moment(a.appointment_date).format('x').toString()) - parseInt(moment(b.appointment_date).format('x').toString()));

    // Save sorted appointments to appropriate list
    switch (appointmentList) {
      case appointmentlistenum.ALL_APPOINTMENTS:
        this.allAppointments.next(sorteddAppointments);
        break;
      case appointmentlistenum.UNCONFIRMED_APPOINTMENTS:
        this.unconfirmedAppointments.next(sorteddAppointments);
        break;
      case appointmentlistenum.UNCONFIRMED_PAST:
        this.allUnconfirmedAppointments.next(sorteddAppointments);
        break;
      case appointmentlistenum.REVIEW_APPOINTMENTS:
        this.reviewAppointments.next(sorteddAppointments);
        break;
      case appointmentlistenum.URGENT_APPOINTMENTS:
        this.urgentAppointments.next(appointments);
        break;
      case appointmentlistenum.ALL_UPCOMING_APPOINTMENTS:
        this.allUpcomingAppointments.next(sorteddAppointments);
        break;
      case appointmentlistenum.ALL_PAST_APPOINTMENTS:
        this.allPastAppointments.next(sorteddAppointments);
        break;
      case appointmentlistenum.PAST_NOT_JOINED:
        this.allPastNotJoinedAppointments.next(sorteddAppointments);
        break;
      case appointmentlistenum.COMING_UP:
        this.allUpcomingSoonAppointments.next(sorteddAppointments);
        break;
      case appointmentlistenum.CONFIRMED_IN_SESSION:
        this.confirmedInSessionAppointments.next(sorteddAppointments);
        break;
      case appointmentlistenum.UNCONFIRMED_ISSUES:
        this.unconfirmedIssuesAppointments.next(sorteddAppointments);
        break;
      default:
        break;
    }
  }

  updateTempAppointment(changes: Partial<Appointment>) {
    const appointment = this.appointment.getValue();

    const newTempAppointment: Appointment = {
      ...appointment,
      ...changes
    }

    this.appointment.next(newTempAppointment);
    return newTempAppointment;
  }
}